import { afterNextRender, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Injector, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Citation } from 'src/app/models/analyst-output.model';
import { CitationPanelService } from 'src/app/services/citation-panel.service';
import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import { shortenCitationLabel } from 'src/app/shared/utils/citation-preview.util';
import { SourceChip } from './analyst-sources.model';
import { countChipsOnFirstRow } from './sources-first-row.util';

const RING_DURATION_MS = 1500;
const CHIP_SELECTOR = 'li.source-chip';
const TOGGLE_SELECTOR = 'li.sources-toggle';

@Component({
  standalone: false,
  selector: 'app-analyst-sources',
  templateUrl: './analyst-sources.component.html',
  styleUrls: ['./analyst-sources.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalystSourcesComponent implements OnInit, OnDestroy {
  @ViewChild('sourcesList') private sourcesList?: ElementRef<HTMLOListElement>;

  chips: SourceChip[] = [];
  expanded = false;
  visibleCount = 0;
  hiddenCount = 0;
  layoutPending = false;
  ringedIndex: number | null = null;

  readonly trackByIndex = trackByIndex;

  private currentSources: readonly Citation[] = [];
  private chipWidths: number[] = [];
  private toggleWidth = 0;
  private observedWidth = 0;
  private observedList?: HTMLOListElement;
  private resizeObserver?: ResizeObserver;
  private relayoutFrame?: number;
  private ringTimer?: ReturnType<typeof setTimeout>;
  private readonly destroy$ = new Subject<void>();

  /** Mapped in the setter rather than through a template method: this component is OnPush, and a
   * method call in the template re-runs on every change-detection pass. */
  @Input()
  set sources(value: readonly Citation[]) {
    this.currentSources = value ?? [];
    this.chips = this.currentSources.map((citation) => ({
      citation,
      label: shortenCitationLabel(citation.label),
      fullLabel: citation.label,
    }));
    this.layoutPending = true;
    afterNextRender(() => this.measure(), { injector: this.injector });
  }

  get sources(): readonly Citation[] {
    return this.currentSources;
  }

  constructor(
    private readonly citationPanelService: CitationPanelService,
    private readonly cdr: ChangeDetectorRef,
    private readonly injector: Injector,
  ) {}

  ngOnInit(): void {
    this.citationPanelService.citationRevealRequested$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (citation) => this.reveal(citation),
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.relayoutFrame ?? 0);
    clearTimeout(this.ringTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded;
  }

  onSourceClick(event: MouseEvent, citation: Citation): void {
    this.citationPanelService.openFromClick(event, citation);
  }

  private reveal(citation: Citation): void {
    const index = this.chips.findIndex((chip) => chip.citation === citation);
    if (index < 0 || this.expanded || (!this.layoutPending && index < this.visibleCount)) {
      return;
    }
    this.expanded = true;
    this.ringedIndex = index;
    clearTimeout(this.ringTimer);
    this.ringTimer = setTimeout(() => {
      this.ringedIndex = null;
      this.cdr.markForCheck();
    }, RING_DURATION_MS);
    afterNextRender(
      () => this.sourcesList?.nativeElement.querySelectorAll(CHIP_SELECTOR)[index]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }),
      { injector: this.injector },
    );
    this.cdr.markForCheck();
  }

  private measure(): void {
    const list = this.sourcesList?.nativeElement;
    if (!list) {
      return;
    }
    this.chipWidths = Array.from(list.querySelectorAll(CHIP_SELECTOR)).map((chip) => chip.getBoundingClientRect().width);
    this.toggleWidth = list.querySelector(TOGGLE_SELECTOR)?.getBoundingClientRect().width ?? 0;
    this.observedWidth = list.clientWidth;
    this.layoutPending = false;
    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    this.visibleCount = countChipsOnFirstRow(this.chipWidths, this.observedWidth, gap, this.toggleWidth);
    this.hiddenCount = this.chips.length - this.visibleCount;
    this.cdr.detectChanges();
    this.observe(list);
  }

  private observe(list: HTMLOListElement): void {
    if (this.observedList === list) {
      return;
    }
    this.resizeObserver ??= new ResizeObserver(() => this.onListResized());
    if (this.observedList) {
      this.resizeObserver.unobserve(this.observedList);
    }
    this.resizeObserver.observe(list);
    this.observedList = list;
  }

  private onListResized(): void {
    if (this.observedList?.clientWidth === this.observedWidth) {
      return;
    }
    cancelAnimationFrame(this.relayoutFrame ?? 0);
    this.relayoutFrame = requestAnimationFrame(() => {
      this.layoutPending = true;
      this.cdr.detectChanges();
      this.measure();
    });
  }
}
